/**
 * Signing — `docs/proposal.md` §8.1 items 4–5, board 7.
 *
 * Two ways, and the customer picks: **Type**, which draws their name in a
 * handwriting face over a ruled line, and **Draw**, a canvas they sign with a
 * finger, a mouse or an Apple Pencil. Both produce the same thing as far as
 * the rest of the page is concerned — a `SignatureForm` — and
 * `missingForAccept` in `shared` is the only thing that decides whether it is
 * enough.
 *
 * **The three faces are fetched when this card mounts, not on every page,
 * and from us.** Caveat, Homemade Apple and Mrs Saint Delafield are display
 * faces used by exactly one card in the product, so they are a lazy chunk
 * rather than something every list screen waits on; and they are
 * self-hosted through `@fontsource` (§13), so a customer's page asks no font
 * CDN for anything while they are signing it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  SIGNATURE_FONTS, SIGNATURE_MIN_BOX, SIGNATURE_MIN_POINTS,
  type SignatureFont, type SignatureForm,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { tokenColour } from '../../lib/token-colour.js'
import { GlyphEraser, GlyphHand, GlyphType } from './glyphs.js'

/** The face each style draws in, and what the button under it is called. */
export const FONT_FAMILY: Record<SignatureFont, string> = {
  caveat: '"Caveat", cursive',
  'homemade-apple': '"Homemade Apple", cursive',
  'mrs-saint-delafield': '"Mrs Saint Delafield", cursive',
}

/** Once per page: three CSS chunks Vite splits out and serves from our own origin. */
let fontsLoading: Promise<unknown> | null = null
function useSignatureFonts(): void {
  useEffect(() => {
    fontsLoading ??= Promise.all([
      import('@fontsource/caveat/600.css'),
      import('@fontsource/homemade-apple/400.css'),
      import('@fontsource/mrs-saint-delafield/400.css'),
    ]).catch(() => undefined)
  }, [])
}

/** Each face at the size board 7 draws it in a 56px tile — they are not one size. */
const TILE_FACE: Record<SignatureFont, string> = {
  caveat: 'text-[27px] font-semibold',
  'homemade-apple': 'text-[19px]',
  'mrs-saint-delafield': 'text-[36px]',
}

export interface SignaturePadProps {
  form: SignatureForm
  onForm: (form: SignatureForm) => void
  /** Called with the PNG when the stroke changes, so the page can send it. */
  onPng: (png: string | null) => void
  disabled?: boolean
}

export function SignaturePad({ form, onForm, onPng, disabled }: SignaturePadProps) {
  useSignatureFonts()
  const first = form.name.trim().split(/\s+/)[0] || 'Name'

  return (
    <div>
      {/* Type · Draw — board 7: full width, 42px segments with their glyphs. */}
      <div role="radiogroup" aria-label="How to sign"
        className="mb-3.5 flex gap-1 rounded-[13px] bg-line-soft p-1">
        {([['typed', 'Type'], ['drawn', 'Draw']] as const).map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={form.mode === mode}
            disabled={disabled}
            onClick={() => onForm({ ...form, mode })}
            className={cn(`flex h-[42px] flex-1 items-center justify-center gap-2 rounded-[10px]
                           text-[14.5px] font-[650]`,
              form.mode === mode
                ? 'bg-surface text-text shadow-[0_1px_3px_rgba(0,0,0,0.12)]'
                : 'text-text-muted')}
          >
            {mode === 'typed' ? <GlyphType size={17} /> : <GlyphHand size={17} />}
            {label}
          </button>
        ))}
      </div>

      {form.mode === 'typed'
        ? (
          <>
            <SignatureBox>
              {form.name.trim()
                ? (
                  <span
                    className="absolute bottom-[38px] left-[50px] right-5 truncate text-[30px]
                               leading-[1.15] text-text"
                    style={{ fontFamily: FONT_FAMILY[form.font] }}
                  >
                    {form.name.trim()}
                  </span>
                  )
                : (
                  <span className="pointer-events-none absolute bottom-[50px] left-[50px] text-[22px]
                                   text-line-strong">
                    Your signature appears here
                  </span>
                  )}
            </SignatureBox>

            <div className="mt-3 grid grid-cols-3 gap-2.5">
              {SIGNATURE_FONTS.map((font) => (
                <button
                  key={font}
                  type="button"
                  aria-pressed={form.font === font}
                  aria-label={`Signature style ${font.replace(/-/g, ' ')}`}
                  disabled={disabled}
                  onClick={() => onForm({ ...form, font })}
                  className={cn('flex h-14 items-center justify-center overflow-hidden rounded-[12px] px-2',
                    form.font === font
                      ? 'border-2 border-brand-ui bg-brand-t1'
                      : 'border-[1.4px] border-line bg-surface')}
                >
                  <span className={cn('truncate leading-none text-text', TILE_FACE[font])}
                    style={{ fontFamily: FONT_FAMILY[font] }}>
                    {first}
                  </span>
                </button>
              ))}
            </div>
          </>
        )
        : <DrawPad form={form} onForm={onForm} onPng={onPng} disabled={disabled} />}
    </div>
  )
}

/**
 * Board 7's box: 168px, a ruled line 34px off the bottom, the × at its left
 * and "Sign above the line" under it. The typed name and the canvas both
 * sit on that one line, so the two ways of signing are one picture.
 */
function SignatureBox({ children, action }: {
  children?: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="relative h-[168px] rounded-[14px] border-[1.6px] border-line bg-surface">
      <div aria-hidden="true"
        className="pointer-events-none absolute bottom-[34px] left-[22px] right-[22px] border-b-[1.6px]
                   border-line-strong" />
      <span aria-hidden="true"
        className="pointer-events-none absolute bottom-10 left-5 text-[22px] font-medium text-text-faint">
        ×
      </span>
      <span className="pointer-events-none absolute bottom-2.5 left-[22px] text-[12px] text-text-faint">
        Sign above the line
      </span>
      {children}
      {action && <div className="absolute right-3 top-3">{action}</div>}
    </div>
  )
}

function DrawPad({ form, onForm, onPng, disabled }: SignaturePadProps) {
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const stroke = useRef<{ x: number; y: number }[]>([])
  /** Every point of the signature, for the bounding box and the count. */
  const all = useRef<{ x: number; y: number }[]>([])
  const [ratio, setRatio] = useState(1)

  const context = useCallback((): CanvasRenderingContext2D | null => {
    const node = canvas.current
    return node ? node.getContext('2d') : null
  }, [])

  /* Size the backing store to the box, once it has one. */
  useEffect(() => {
    const node = canvas.current
    if (!node) return
    const scale = Math.min(2, window.devicePixelRatio || 1)
    setRatio(scale)
    const box = node.getBoundingClientRect()
    node.width = Math.round(box.width * scale)
    node.height = Math.round(box.height * scale)
    const ctx = node.getContext('2d')
    if (!ctx) return
    ctx.scale(scale, scale)
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    /*
     * **A canvas paints to a bitmap and cannot read a CSS class**, which is
     * the same wall MapLibre hit (`property-maps-v2.md`): the ink colour has
     * to be a string. `tokenColour` reads the token off the document, so a
     * signature drawn in dark mode is drawn in that mode's ink rather than in
     * a hex somebody typed here and nothing can follow.
     */
    ctx.strokeStyle = tokenColour('text')
  }, [])

  const report = useCallback(() => {
    const points = all.current
    if (points.length === 0) {
      onForm({ ...form, points: 0, box: { width: 0, height: 0 } })
      onPng(null)
      return
    }
    const xs = points.map((one) => one.x)
    const ys = points.map((one) => one.y)
    onForm({
      ...form,
      points: points.length,
      box: {
        width: Math.max(...xs) - Math.min(...xs),
        height: Math.max(...ys) - Math.min(...ys),
      },
    })
    onPng(canvas.current?.toDataURL('image/png') ?? null)
  }, [form, onForm, onPng])

  const pointOf = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - box.left, y: event.clientY - box.top }
  }

  const down = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return
    event.currentTarget.setPointerCapture(event.pointerId)
    drawing.current = true
    const point = pointOf(event)
    stroke.current = [point]
    all.current.push(point)
  }

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    event.preventDefault()
    const ctx = context()
    if (!ctx) return

    const point = pointOf(event)
    stroke.current.push(point)
    all.current.push(point)

    /*
     * A quadratic through the midpoints: each segment's control point is the
     * previous sample, so consecutive curves meet with a continuous tangent.
     * Straight lines between raw samples look like a seismograph.
     */
    const points = stroke.current
    const n = points.length
    if (n < 3) return
    const a = points[n - 3]!
    const b = points[n - 2]!
    const c = points[n - 1]!
    ctx.beginPath()
    ctx.moveTo((a.x + b.x) / 2, (a.y + b.y) / 2)
    ctx.quadraticCurveTo(b.x, b.y, (b.x + c.x) / 2, (b.y + c.y) / 2)
    ctx.stroke()
  }

  const up = () => {
    if (!drawing.current) return
    drawing.current = false
    stroke.current = []
    report()
  }

  const clear = () => {
    const node = canvas.current
    const ctx = context()
    if (node && ctx) ctx.clearRect(0, 0, node.width / ratio, node.height / ratio)
    all.current = []
    stroke.current = []
    report()
  }

  const empty = all.current.length === 0
  const enough = form.points >= SIGNATURE_MIN_POINTS
    && form.box.width >= SIGNATURE_MIN_BOX.width
    && form.box.height >= SIGNATURE_MIN_BOX.height

  return (
    <SignatureBox
      action={!empty && (
        /* A 32px chip in a 44px target (board 7 cell 2). */
        <button
          type="button"
          onClick={clear}
          className="-m-1.5 flex min-h-[44px] items-center p-1.5"
        >
          <span className="flex h-8 items-center gap-1.5 rounded-[9px] bg-bg px-3 text-[13px]
                           font-semibold text-text-body">
            <GlyphEraser size={15} />
            Clear
          </span>
        </button>
      )}
    >
      <canvas
        ref={canvas}
        aria-label="Sign here"
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
        className="absolute inset-0 h-full w-full touch-none rounded-[14px]"
      />
      {empty && (
        <span className="pointer-events-none absolute bottom-[50px] left-[50px] text-[22px]
                         text-line-strong">
          Sign here with your finger or mouse
        </span>
      )}
      {!empty && !enough && (
        <span className="pointer-events-none absolute bottom-2.5 right-[22px] text-[12px] text-warn">
          A little more — that is a mark rather than a signature.
        </span>
      )}
    </SignatureBox>
  )
}
